/**
 * Home Feature - English (en-US)
 */

import type { HomeLocale } from './types';

const enUS: HomeLocale = {
    home: {
        title: 'Accelerate Patent Drafting with AI',
        subtitle: 'Intelligently complete the full patent documentation process from invention disclosures to office action responses',

        tabs: {
            patent: 'Patent Application',
            disclosure: 'Invention Disclosure',
            oar: 'Office Action Response',
        },

        cardTitles: {
            patent: 'Patent Application',
            disclosure: 'Invention Disclosure',
            oar: 'Office Action Response',
        },

        placeholders: {
            patent: 'Describe your technical solution, the problem it addresses, and its innovative aspects.\n\nYou can also type / to use a template or upload files.',
            disclosure: 'Describe your invention idea, the technical problem it solves, and key innovations.\n\nYou can also type / to use a template or upload files.',
            oar: 'Paste the office action notice content here, or describe the rejection reasons you need to address.',
        },

        placeholder: {
            default: 'Describe your invention or type / to use a template',
            template: 'Type / to browse patent templates',
            example1: 'Try: A machine learning system for image recognition...',
            example2: 'Try: An improved battery design with higher capacity...',
            example3: 'Example: / USPTO template for software patents',
        },

        workMode: {
            agent: 'Agent',
            agentDescription: 'Auto complete',
            copilot: 'Copilot',
            copilotDescription: 'Review each step',
        },

        actions: {
            addFiles: 'Add Files',
            addFilesTooltip: 'Upload files (PDF, Word, TXT)',
            readOnlyTip: 'You have read-only permission and cannot upload files',
            dropToAdd: 'Drop to add files',
            style: 'Style',
            styleTooltip: 'Choose drafting style',
            template: 'Template',
            templateTooltip: 'Choose patent template',
            voiceInput: 'Voice Input',
            voiceInputTooltip: 'Voice input',
            voiceInputSoon: 'Voice input coming soon...',
            generate: 'Start',
            loggingIn: 'Logging in...',
            startDrafting: 'Start Drafting',
            draftingStyle: 'Drafting Style',
            selectTemplate: 'Select Template',
            removeFile: 'Remove',
            loginNow: 'Log In',
            changeTemplate: 'Click to change template',
            featureDisabledTip: 'You do not have permission to use this feature. Please contact admin.',
            personalQuotaExhaustedTip: 'Your personal quota is exhausted. Please contact admin for more quota.',
        },

        upload: {
            dragOrClick: 'Drag files here or click to upload',
            supportedFormats: 'Supports PDF, Word, TXT, and image files',
        },

        quickStart: {
            title: 'Quick start common drafting tasks:',
            disclosure: 'Draft Disclosure',
            specification: 'Draft Specification',
            claims: 'Draft Claims',
            oar: 'Draft Office Action Response',
            figures: 'Design Figures',
        },

        messages: {
            filesAdded: 'Added {count} file(s)',
            pleaseEnterContent: 'Please enter content or upload files',
            pleaseUploadFile: 'Please upload office action file',
            generating: 'Generating patent document...',
            navigationFailed: 'Navigation failed',
            styleApplied: 'Applied style: {name}',
            templateApplied: 'Template selected: {name}',
            mentionApplied: 'Selected {type}: {name}',
            templateLoading: 'Loading template: {name}',
            submitFailed: 'Submit failed, please try again',
            invalidFile: 'Invalid file',
            uploadFailed: 'File upload failed, please try again',
            filesUploading: 'Files are uploading, please wait',
            fileStatusUploading: 'Uploading...',
            fileStatusFailed: 'Upload failed',
            someFilesNotUploaded: 'Some files failed to upload, please upload again',
            emptyInput: 'Please enter content or upload files',
            emptyOarInput: 'Please enter patent application number or upload files',
            fileProcessed: 'File {name} processed successfully',
            fileProcessFailed: 'File {name} processing failed',
            fileProcessingTimeout: 'File processing timeout, please re-upload',
            filesProcessing: 'Files are being processed, please wait...',
            filesNotReady: 'Some files are still being processed, please wait before creating task',
            fileAlreadyExists: 'File {name} already exists, using existing file',
            defaultPromptForPatent: 'Please parse the uploaded documents, generate technical disclosure analysis report and patent application documents.',
            defaultPromptForDisclosure: 'Process the files following the standard invention disclosure analysis workflow to generate an invention disclosure',
            loginRequired: 'Please log in to start drafting. Your work will be saved to your account.',
        },

        // Unified initial prompts configuration (automatically selected based on task type and scenario)
        initialPrompts: {
            patent_draft: {
                withFiles: 'Please parse the uploaded documents, generate technical disclosure analysis report and patent application documents.',
                empty: 'Please start drafting the patent application',
            },
            disclosure: {
                withFiles: 'Process the files following the standard invention disclosure analysis workflow to generate an invention disclosure',
                empty: 'Please start drafting the invention disclosure',
            },
            prosecution: {
                withPatentNumber: 'Start analyzing office action (Application No.: {patentNumber})',
                withFiles: 'Start analyzing office action',
                empty: 'Please analyze the office action and generate a response',
            },
        },

        mention: {
            title: 'Templates',
            selectType: 'Select Type',
            styleTitle: 'Style',
            priorArt: 'Prior Art',
            file: 'File',
            document: 'Document',
            figure: 'Figure',
            loading: 'Loading...',
            navigate: 'Navigate',
            select: 'Select',
            close: 'Close',
            noResults: 'No templates found for "{query}"',
            tryDifferent: 'Try a different search term',
            applicationDate: 'Application:',
            publicationDate: 'Publication:',
            docTypes: {
                patent_draft: 'Patent Drafting',
                disclosure: 'Technical Disclosure',
                prosecution: 'Office Action Response',
            },
            docStatus: {
                draft: 'Draft',
                in_review: 'In Review',
                approved: 'Approved',
                filed: 'Filed',
            },
            types: {
                template: {
                    label: 'Template',
                    desc: 'Patent application template',
                },
                style: {
                    label: 'Writing Style',
                    desc: 'Writing style settings',
                },
                priorArt: {
                    label: 'Prior Art',
                    desc: 'Prior art patent',
                },
                file: {
                    label: 'File',
                    desc: 'Attachment file',
                },
                document: {
                    label: 'Document',
                    desc: 'Project document',
                },
                figure: {
                    label: 'Figure',
                    desc: 'Project figure',
                },
            },
            templates: {
                usptoUtilityPatent: 'USPTO Utility Patent',
                usptoSoftwarePatent: 'USPTO Software Patent',
                epoPatentApplication: 'EPO Patent Application',
                pctInternationalApplication: 'PCT International Application',
                cnipaPatentApplication: 'CNIPA Patent Application',
            },
        },

        tooltips: {
            uploadFile: 'Upload file',
            voiceInput: 'Voice input',
        },

        patent: {
            jurisdiction: 'Please select application region',
        },

        oar: {
            title: 'Office Action Response',
            projectTitle: 'Office Action Response - {patentNumber}',
            projectTitleWithoutNumber: 'Office Action Response',
            initialPrompt: 'Start analyzing office action',
            initialPromptWithNumber: 'Start analyzing office action (Application No.: {patentNumber})',
            confirmMessage: 'OAR analysis results confirmed, please continue processing',
            oarExtractList: 'OAR Extract List',
            rejectionTableTitle: 'Rejection Analysis Table',
            opinionStatementTitle: 'Response Statement',
            amendedClaimsTitle: 'Replacement Claims',
            amendmentComparisonTitle: 'Comparison',
            rejectionAnalysis: {
                title: 'Rejection Analysis',
                status: {
                    extracting: 'Extracting data',
                    processing: 'Processing',
                },
                button: {
                    confirm: 'Confirm and Continue',
                    saving: 'Saving',
                    addClause: 'Add Clause',
                    showMore: 'Show more',
                    showLess: 'Show less',
                    export: 'Export Excel',
                    exportSheetName: 'Rejection Analysis',
                    viewDetail: 'View the Logical Chain',
                    delete: 'Delete',
                },
                detail: {
                    drawerTitle: 'Analysis Detail',
                    examinerOpinion: 'Examiner Opinion',
                    featureComparison: 'Feature Comparison',
                    claimFeature: 'Claim Feature',
                    priorArtFeature: 'Prior Art Feature',
                    priorArtLabel: 'Prior Art {no} · {name}',
                    noPriorArt: 'No prior art',
                    strategy: 'Response Strategy',
                    strategyReason: 'Strategy Basis',
                    prevItem: 'Previous',
                    nextItem: 'Next',
                },
                direction: {
                    agree: 'Agree',
                    unsure: 'Unsure',
                    disagree: 'Disagree',
                },
                basicInfo: {
                    title: 'Basic Info',
                    application: 'Application No.',
                    oaNo: 'OA No.',
                    type: 'Type',
                    examiner: 'Examiner',
                    issueDate: 'Issue Date',
                    priorArt: 'Prior Art',
                    applicationFiles: 'Application Files',
                },
                workbench: {
                    caseTitleFallback: 'Patent Response Strategy',
                    examinerConclusionTitle: 'Office Action Summary',
                    examinerConclusionPrefix: 'Examiner\'s position: {conclusion}',
                    patentTypeInvention: 'Invention Patent',
                    patentTypeUtilityModel: 'Utility Model',
                    viewMaterials: 'Materials & Prosecution History',
                    officeActionHistory: 'Prosecution History',
                    round: 'Office Action Round',
                    current: 'Current',
                    claimsFile: 'Claims Under Examination',
                    overallStrategy: 'Overall Response Strategy',
                    overallFallback: 'No case-level integration is available. Review each issue strategy below.',
                    sectionTabsLabel: 'Rejection analysis views',
                    caseOverviewTab: 'Case Overview',
                    documentComparisonTab: 'Feature Analysis',
                    issueStrategiesTab: 'Issue Strategies',
                    issueView: 'By Rejection Issue',
                    documentComparison: 'Distinguishing Feature Summary',
                    applicationColumnTitle: 'Application',
                    applicationFeatureColumnSubtitle: 'Claim Technical Features',
                    priorArtColumnTitle: 'Reference {index}',
                    naturalFeatureTitle: 'Technical Feature {index}',
                    comparisonConflict: 'Conflicting Conclusions',
                    comparisonNeedsConfirmation: 'Inconsistent Findings',
                    comparisonFullyCorresponding: 'Fully Corresponding',
                    comparisonPartiallyCorresponding: 'Partially Disclosed',
                    comparisonClearlyDifferent: 'Clearly Different',
                    comparisonNotFound: 'No Corresponding Disclosure Found',
                    comparisonNeedsVerification: 'Insufficient Evidence',
                    comparisonNoConclusion: 'No Conclusion',
                    judgmentCount: '{count} findings',
                    pendingJudgmentCount: '{count} need verification',
                    viewAllJudgments: 'View all {count} findings',
                    horizontalScrollLabel: 'Scroll horizontally through all references',
                    viewComparisonDetails: 'View Details',
                    comparisonDetails: 'Feature Comparison Details',
                    additionalDifferences: '{count} more finding(s)',
                    notCompared: 'Not compared for this feature',
                    noDocumentComparison: 'No distinguishing features are available for aggregation.',
                    issueCount: 'Issues',
                    amendmentCount: 'Amendments',
                    riskCount: 'Risks',
                    keyAmendments: 'Claim Amendments',
                    multipleAmendmentsWarning: 'This claim has multiple issue-level proposals. Consolidate them before generating the response.',
                    viewIssueStrategy: 'View Issue Strategy',
                    issueNavigation: 'Issue Navigation',
                    issueStrategy: 'Claim Strategies',
                    issue: 'Issue',
                    responseMethod: 'Response Method',
                    strategySummary: 'Strategy Summary',
                    recommendedResponse: 'Recommended Response',
                    whetherAmendClaims: 'Amend Claims',
                    amendmentCore: 'Core Amendment',
                    mainReasons: 'Main Reasons',
                    risksOrPending: 'Risks or Items to Confirm',
                    noRisksOrPending: 'No risks or items to confirm',
                    detailAnalysis: 'Detailed Analysis',
                    examinerReasoningDetails: 'Examiner Reasoning and Disputed Points',
                    amendmentDetails: 'Detailed Amendment and Full Claims',
                    inventiveStepDetails: 'Inventive-Step Analysis',
                    problemSolutionApproach: 'Problem-Solution Approach',
                    comparisonEvidenceDetails: 'Key Features, References, and Drawing Evidence',
                    supportingInformationDetails: 'Other Supporting Information',
                    claimAmendment: 'Claim Amendment',
                    amendmentProvided: 'Amendment Provided',
                    noAmendment: 'No Amendment',
                    examinerStartingPoint: 'Examiner\'s Starting Point',
                    amendmentPlan: 'Claim Amendment Plan',
                    amendmentPlanHint: 'Review the amendment, original basis, and scope impact.',
                    amendmentMethod: 'Amendment Method',
                    scopeImpact: 'Scope Impact',
                    amendmentBasis: 'Amendment Basis',
                    amendmentReason: 'Reason for Amendment',
                    viewAmendedClaims: 'View Full Amended Claims',
                    threeStepMethod: 'Inventive-Step Analysis',
                    closestPriorArt: 'Identify the Closest Prior Art',
                    differencesAndProblem: 'Identify Differences and the Technical Problem',
                    distinguishingFeatures: 'Distinguishing Features',
                    technicalEffects: 'Technical Effects in the Specification',
                    objectiveTechnicalProblem: 'Objective Technical Problem',
                    objectiveProblemGap: 'Not yet determined. Please confirm based on the distinguishing features and their technical effects.',
                    technicalMotivation: 'Determine Whether the Prior Art Provides Motivation',
                    argumentPath: 'Technical Motivation and Combination Analysis',
                    responseConclusion: 'Response Conclusion',
                    featureComparison: 'Key Feature Comparison',
                    featureComparisonHint: 'Align each claim feature with the prior-art conclusion.',
                    drawingEvidence: 'Related Drawing Evidence',
                    drawingEvidenceHint: 'Application and prior-art images related to the current features and response strategy.',
                    applicationFeature: 'Application Claim Feature',
                    priorArtConclusion: 'Prior-Art Comparison Conclusion',
                    essentialFeature: 'Essential Feature',
                    viewPriorArtExcerpt: 'View Prior-Art Excerpt',
                    priorArtLocation: 'Prior-Art Evidence Location',
                    embodimentBasis: 'View Same-Embodiment Basis',
                    noComparison: 'No feature comparison is available.',
                    risks: 'Relevant Risks',
                    risksHint: 'Case-specific risks requiring focused review.',
                    supportingInformation: 'Supporting Information',
                    supportingInformationHint: 'Application summary, effects, prior art, and verification notes.',
                    applicationSummary: 'Application Summary',
                    priorArtDocument: 'Prior Art',
                    viewClaimsSource: 'View Claims Under Examination',
                    verificationNotes: 'Verification Notes',
                    historyConsistency: 'History Consistency',
                    noPriorArt: 'No prior-art summary is available.',
                    notProvided: 'Not provided',
                    edit: 'Edit',
                    editOverallStrategy: 'Edit Overall Response Strategy',
                    editFeatureSummary: 'Edit feature',
                    editComparison: 'Edit conclusion',
                    editingInPlace: 'Editing in place',
                    markdownSupported: 'Markdown supported',
                    save: 'Save',
                    cancel: 'Cancel',
                    editResponseStrategy: 'Edit response strategy',
                    strategyReason: 'Strategy rationale',
                    amendedClaimText: 'Complete amended claim text',
                    amendmentSourceType: 'Amendment source',
                    sourceClaims: 'Source claims (comma-separated)',
                    sourceLocations: 'Source locations (semicolon-separated)',
                    whyDependentInsufficient: 'Why merging dependent claims is insufficient',
                    whyAmendmentOneInsufficient: 'Why the first-level amendment is insufficient',
                    oneRiskPerLine: 'One risk per line, up to four',
                    completeRequiredFields: 'Complete the required fields before saving',
                    markEssential: 'Mark as essential',
                    evidenceReadonlyHint: 'Evidence text and locations remain read-only',
                    generateResponseDocument: 'Generate response document',
                    regenerateStrategy: 'Regenerate response strategy',
                    regenerateScope: 'Regeneration scope',
                    currentIssue: 'Current issue',
                    allIssues: 'All issues',
                    regenerateRequirement: 'Additional requirement',
                    regenerateRequirementPlaceholder: 'For example: recheck ISSUE_001 distinctions and amendment necessity',
                    regenerateOverwriteHint: 'Regeneration replaces the selected issue strategies; case-level manual wording is preserved.',
                    confirmRegenerate: 'Regenerate',
                    saveEditorFirst: 'Save or cancel the current editor first',
                    openTargetDocumentFirst: 'Open or create a target response document first',
                    generateChatMessage: 'The user has finished editing the rejection-analysis page and confirms the current distinguishing features and response strategies. The rejection analysis data ID is {rejectionDataId}. {refreshInstruction}Generate the response document from the current data and write it directly to the bound target document. Do not ask for confirmation again and do not regenerate the per-issue strategies.',
                    refreshIntegrationBeforeGenerate: 'The per-issue strategies were edited manually. Before generating the document, refresh only the case-level integration with integrate_only and preserve the manual edits. ',
                    regenerateChatMessage: 'The user requests regeneration of the rejection response strategy. The rejection analysis data ID is {rejectionDataId}; the regeneration scope is {scope}; the additional requirement is {requirement}. Reanalyze that scope and show the latest rejection-analysis result.',
                    noAdditionalRequirement: 'none',
                    sourceType: {
                        dependentClaim: 'Dependent claim',
                        claim: 'Other claim',
                        specification: 'Specification',
                        drawing: 'Drawing',
                        multipleSources: 'Multiple sources',
                    },
                    decision: {
                        argueNoAmend: 'Argue Without Amendment',
                        mergeDependent: 'Merge Dependent Claim and Argue',
                        amendOne: 'Narrow Claims and Argue',
                        amendTwo: 'Further Narrow Claims and Argue',
                        legacy: 'Use Existing Analysis Strategy',
                    },
                    comparisonStatus: {
                        fullyCorresponding: 'Fully Corresponding',
                        partiallyCorresponding: 'Partially Disclosed',
                        clearlyDifferent: 'Clearly Different',
                        notFound: 'No Corresponding Disclosure Found',
                        evidenceMissing: 'Evidence Missing',
                        unableToVerify: 'Unable to Verify',
                        explicitlyDisclosed: 'Explicitly Disclosed',
                        necessarilyImplied: 'Necessarily Implied',
                        partiallyDisclosed: 'Partially Disclosed',
                        notDisclosed: 'Not Disclosed',
                    },
                    evidencePlaceholder: {
                        notLocatedInSource: 'Not located in the source text',
                        validLocationMissing: 'No valid location provided',
                        priorArtFullTextMissing: 'Prior-art full text was not provided; disclosure could not be verified',
                        citedRejectionEvidenceUnavailable: 'The rejection evidence cited by the model is unavailable',
                        anchorDiagnostic: {
                            startNotFound: 'Start anchor was not found: {anchor}',
                            endNotFound: 'End anchor was not found: {anchor}',
                            startMatchedOnce: 'Start anchor matched once (offset={offset}): {anchor}',
                            endMatchedOnce: 'End anchor matched once (offset={offset}): {anchor}',
                            startMatchedMany: 'Start anchor matched {count} times (offsets={offsets}): {anchor}',
                            endMatchedMany: 'End anchor matched {count} times (offsets={offsets}): {anchor}',
                        },
                    },
                },
                tabs: {
                    all: 'All',
                },
                tableHeaders: {
                    claim: 'Claim',
                    statute: 'Statute',
                    claimAndStatute: 'Claim/Statute',
                    claimElement: 'Claim Element',
                    examinerReasoning: 'Examiner Reasoning',
                    aiAnalysis: 'AI Analysis',
                    strategy: 'Response Strategy',
                },
                statusPills: {
                    agree: 'Agree',
                    unsure: 'Unsure',
                    disagree: 'Disagree',
                },
                labels: {
                    direction: 'Response Direction',
                    strategy: 'Response Strategy',
                    claimAmendment: 'Claim Amendment',
                    amendmentMethod: 'Amendment Method',
                    amendmentBasis: 'Amendment Basis',
                },
                needAmendment: {
                    yes: 'Amend',
                    no: 'No Amend',
                    optional: 'AI Decide',
                },
                tooltips: {
                    direction: 'Whether to agree with the examiner\'s opinion',
                    strategy: 'Response strategy to the rejection',
                    claimAmendment: 'Whether claims need amendment',
                },
                placeholder: {
                    edit: 'Click to edit...',
                    claimElement: 'Enter claim element...',
                    examinerReasoning: 'Enter examiner reasoning...',
                    strategy: 'Enter response strategy...',
                    amendmentMethod: 'Enter amendment method...',
                    amendmentBasis: 'Enter amendment basis...',
                },
                emptyState: {
                    noData: 'No rejection data',
                    hint: 'Rejection analysis will be displayed here after extraction',
                },
                exportSheetNames: {
                    rejectionAnalysis: 'Rejection Analysis',
                    keyFeatureComparison: 'Key Feature Comparison',
                },
                exportHeaders: {
                    issue: 'Issue',
                    claim: 'Claim',
                    statute: 'Statute',
                    claimElement: 'Claim Element',
                    examinerReasoning: 'Examiner Reasoning',
                    direction: 'Response Direction',
                    strategy: 'Response Strategy',
                    distinguishingFeatures: 'Distinguishing Features',
                    priorArt: 'Prior Art',
                    needAmendment: 'Need Amendment',
                    amendmentMethod: 'Amendment Method',
                    amendmentBasis: 'Amendment Basis',
                    strategyReason: 'Strategy Reason',
                    scopeImpact: 'Scope Impact',
                    risks: 'Risks',
                    featureId: 'Feature ID',
                    applicationLocation: 'Application Location',
                    comparisonStatus: 'Comparison Status',
                    specificDifference: 'Specific Difference',
                    priorArtLocation: 'Prior-Art Location',
                },
                message: {
                    noDataId: 'Unable to get data ID',
                    noData: 'No data available',
                    saveSuccess: 'Saved successfully',
                    saveFailed: 'Save failed',
                    deleteSuccess: 'Deleted successfully',
                },
            },
            region: {
                uspto: 'USPTO',
                epo: 'EPO',
                cnipa: 'CNIPA',
                jpo: 'JPO',
            },
            patentNumber: {
                label: 'Patent Application Number',
                placeholder: 'Enter the Application Number of the Patent under examination to retrieve the latest office action',
            },
            uploadAction: {
                title: 'Upload Office Action',
                choose: 'Choose File',
                description: 'Upload the PDF/Word file of the Office Action/Opinion that requires a response',
                uploading: 'Uploading',
                addMore: 'Continue uploading more files',
            },
            uploadedFiles: {
                title: 'Uploaded Files ({count})',
            },
            fileStatus: {
                preparing: 'Preparing file...',
                analyzing: 'Analyzing content...',
                finalizing: 'Almost done...',
                processing: 'Processing...',
                estimatedTime: 'Estimated 1-2 minutes',
                completed: 'Completed',
                failed: 'Parsing failed',
            },
            actions: {
                agent: 'Agent',
                copilot: 'Copilot',
                start: 'Start',
            },
            search: {
                failed: 'Patent search failed, please try again',
                notFound: 'Patent not found',
            },
            patent: {
                publicationNumber: 'Publication Number:',
                publicationDate: 'Publication Date:',
                currentApplicant: '[Current] Applicant:',
            },
            download: {
                searching: 'Searching for office action PDF...',
                downloading: 'PDF download started, please check browser downloads',
                success: 'PDF downloaded successfully',
                failed: 'PDF download failed, please try again',
                notFound: 'Office action PDF not found, you can manually upload',
                visitUsptoWebsite: 'Visit USPTO website to download',
            },
            unsupportedRegion: 'This region is not yet supported',
            prosecutionTip: 'Enter the Patent Application Number / Upload the Office Action/Opinion PDF to get started',
            rejectionDetail: {
                drawerTitle: 'Rejection Detail',
                examinerOpinion: 'Examiner Opinion',
                featureComparison: 'Feature Comparison',
                claimFeature: 'Claim Feature',
                specificationSource: 'Specification Source',
                priorArtFeature: 'Prior Art Feature',
                priorArtLabel: 'Prior Art {no} ({name})',
                noPriorArt: 'No prior art information available',
                priorArtSource: '{name} Source',
                distinguishingFeatures: 'Distinguishing Features',
                aiStrategy: 'AI Response Strategy',
                strategyReason: 'Strategy Basis',
                crossValidation: 'Cross Validation',
                prevItem: 'Previous',
                nextItem: 'Next',
                direction: {
                    agree: 'Agree',
                    unsure: 'Unsure',
                    disagree: 'Disagree',
                },
                claimAmendment: {
                    title: 'Claim Amendment Suggestion',
                    needAmendment: 'Need Amendment',
                    yes: 'Amend',
                    no: 'No Amend',
                    optional: 'AI Decide',
                    method: 'Amendment Method',
                    basis: 'Amendment Basis',
                },
            },
        },

        draftingStyle: {
            title: 'Select Drafting Style',
            stylesHeader: 'Styles',
            previewHeader: 'Preview',
            cancel: 'Cancel',
            apply: 'Apply Style',
            loading: 'Loading...',
            loadingPreview: 'Loading preview...',
            loadError: 'Failed to load styles',
            loadDetailError: 'Failed to load style detail',
            noStyles: 'No styles available',
            noPreview: 'No preview available',
            pleaseSelect: 'Please select a style first',
            used: 'Used {count} times',
            styles: {
                standard: {
                    name: 'Standard Format',
                    description: 'Traditional patent format with numbered paragraphs and formal language',
                },
                detailed: {
                    name: 'Detailed Technical',
                    description: 'Comprehensive technical descriptions with extensive embodiment details',
                },
                concise: {
                    name: 'Concise Claims-Focused',
                    description: 'Streamlined format emphasizing clear and strong claim language',
                },
                international: {
                    name: 'PCT International',
                    description: 'Format optimized for international patent applications under PCT',
                },
                continuation: {
                    name: 'Continuation Application',
                    description: 'Format for continuation or divisional applications referencing parent applications',
                },
            },
        },

        templateSelection: {
            title: 'Select Template',
            templatesHeader: 'Templates',
            previewHeader: 'Preview',
            cancel: 'Cancel',
            apply: 'Apply Template',
            loading: 'Loading...',
            loadingPreview: 'Loading preview...',
            loadError: 'Failed to load templates',
            loadDetailError: 'Failed to load template detail',
            noTemplates: 'No templates available',
            noPreview: 'No preview available',
            pleaseSelect: 'Please select a template first',
        },

        documentModifications: {
            title: 'Document Modifications Applied',
            summary: '{count} modifications in total',
            streamingSummary: 'Generating edits, {count} detected',
            streamingTitle: 'AI is editing the document',
            streamingDesc: 'Receiving modification content. The backend will merge it and refresh the latest version when complete.',
            streamPreview: 'Live modification stream',
            appliedSummary: '{count} modifications applied',
            appliedTitle: 'Modifications applied automatically',
            appliedDesc: 'The backend has merged the edits and created a new version. The editor refreshes to the latest content.',
            emptyState: 'No modification details',
            noContent: 'No new content',
            oldContent: 'Old text:',
            actions: {
                insert: 'Insert',
                insertBefore: 'Insert Before',
                insertInside: 'Insert Inside',
                deleteInside: 'Delete Inside',
                replace: 'Replace',
                delete: 'Delete',
            },
            tableHeaders: {
                index: '#',
                documentId: 'Document ID',
                action: 'Action',
                dataId: 'data-id',
                content: 'Modified Content',
                oldContent: 'Old Text',
            },
            exportExcel: 'Export Excel',
        },
    },
};

export default enUS;
